// File: js/money/stock-fold.js
//
// The stock projection. Imports nothing but its sibling dedupe helper (spec §8).
//
// Spec §4, the core new rule: **an offline oversell is a fact about the world, not an
// error.** If Caisse B sells the last 3 boxes offline while another device sells 2, five
// boxes have physically left the shelf. So a negative position is REPORTED in
// `anomalies[]` — never clamped to zero (that would invent stock), never raised as an
// error (that is the `current_stock >= 0` CHECK the rewrite drops, which converts a real
// sale into lost cash).
//
// `drugs.current_stock` is dropped: stock is fold(events), never a stored balance
// (invariant 1), which also kills the audit C2 read-modify-write race where two cashiers
// sold 5 units from a stock of 10 and the column ended at 8.

import { dedupeById } from './dedupe.js';

/** Kinds that move goods. `discount` moves money only — nothing returns to the shelf. */
const STOCK_KINDS = new Set(['sale', 'void', 'restock', 'stock_adjustment']);

const KNOWN_KINDS = new Set(['sale', 'void', 'discount', 'restock', 'stock_adjustment']);

/**
 * @typedef {Object} StockLine
 * @property {string} item_id
 * @property {'revenue_type'|'drug'} item_kind
 * @property {number} qty Integer; may be negative on a `stock_adjustment` only.
 */

/**
 * @typedef {Object} StockAnomaly
 * @property {'event'|'line'|'position'} scope
 * @property {string} reason kebab-case code.
 * @property {string|null} [id] Ledger event id, when the anomaly is about one event.
 * @property {string|null} [itemId] Drug id, when the anomaly is about one drug.
 * @property {number} [position] Folded position, for `negative-position`.
 * @property {string[]} [events] Ids of the events that moved this drug, in fold order.
 * @property {string|null} [crossedAtEventId] The event that took the position below zero.
 */

/**
 * @typedef {Object} StockFold
 * @property {Object<string, number>} positions Folded quantity per drug id.
 * @property {StockAnomaly[]} anomalies Negative positions and unusable rows.
 */

/**
 * Reads the stock-bearing lines of an event.
 * @param {*} event
 * @returns {StockLine[]|null} `null` when the event carries no usable `items` array.
 */
function linesOf(event) {
    return Array.isArray(event && event.items) && event.items.length > 0 ? event.items : null;
}

/**
 * Folds ledger events into per-drug positions.
 *
 * `sale` `−qty` · `void` `+qty` · `restock` `+qty` · `stock_adjustment` `±qty` (spec §4.1).
 * A void restores **every** line by folding `items` — audit H5: the current code guards
 * on the single-line `drug_id`/`quantity` pair, so voiding a 5-drug sale reverses the
 * money and loses the drugs from inventory forever.
 *
 * Never throws for a data-level problem (invariant 7); unusable rows land in `anomalies[]`.
 *
 * @param {Array<Object>} events Ledger rows in chronological order. Final positions are
 *   order-independent; only the `crossedAtEventId` attribution depends on the order given.
 * @returns {StockFold}
 * @throws {TypeError} If `events` is not an array (programmer error).
 */
export function foldStock(events) {
    const unique = dedupeById(events);

    /** @type {Object<string, number>} */
    const positions = {};
    /** @type {StockAnomaly[]} */
    const anomalies = [];
    /** @type {Map<string, string[]>} */
    const touchedBy = new Map();
    /** @type {Map<string, string>} */
    const crossedAt = new Map();

    // A void carries `reverses_id`, not necessarily the lines it restores, so the sale's
    // own items are the authority when the void has none of its own (spec §4.5).
    const saleById = new Map();
    for (const event of unique) {
        if (event && typeof event === 'object' && event.kind === 'sale' && event.id != null) {
            saleById.set(event.id, event);
        }
    }

    /**
     * @param {string} itemId
     * @param {number} delta
     * @param {string} eventId
     */
    function applyDelta(itemId, delta, eventId) {
        const before = positions[itemId] || 0;
        const after = before + delta;
        positions[itemId] = after;

        const touched = touchedBy.get(itemId) || [];
        touched.push(eventId);
        touchedBy.set(itemId, touched);

        if (before >= 0 && after < 0 && !crossedAt.has(itemId)) {
            crossedAt.set(itemId, eventId);
        }
    }

    for (const event of unique) {
        if (!event || typeof event !== 'object') {
            anomalies.push({ scope: 'event', id: null, reason: 'malformed-event' });
            continue;
        }
        if (event.id == null) {
            anomalies.push({ scope: 'event', id: null, reason: 'missing-id' });
            continue;
        }
        if (typeof event.kind !== 'string' || !KNOWN_KINDS.has(event.kind)) {
            anomalies.push({ scope: 'event', id: event.id, reason: 'unknown-kind' });
            continue;
        }
        if (!STOCK_KINDS.has(event.kind)) continue; // `discount`: money only.

        let lines = linesOf(event);
        if (event.kind === 'void' && !lines) {
            const reversed = saleById.get(event.reverses_id);
            lines = reversed ? linesOf(reversed) : null;
            if (!lines && !reversed) {
                // The sale is outside this event set — restoring nothing would silently
                // leave the shelf short. Say so rather than guess.
                anomalies.push({ scope: 'event', id: event.id, reason: 'void-target-not-in-set' });
                continue;
            }
        }
        if (!lines) {
            // A regie sale legitimately has no drug lines; a pharmacy sale or a stock
            // movement with none is a row we cannot fold, and silence would be a lie.
            if (event.kind === 'sale' && event.channel !== 'pharmacy') continue;
            anomalies.push({ scope: 'event', id: event.id, reason: 'missing-items' });
            continue;
        }

        for (const line of lines) {
            if (!line || typeof line !== 'object') {
                anomalies.push({ scope: 'line', id: event.id, itemId: null, reason: 'malformed-line' });
                continue;
            }
            if (line.item_kind === 'revenue_type') continue; // Services hold no stock.
            if (line.item_kind !== 'drug') {
                anomalies.push({ scope: 'line', id: event.id, itemId: line.item_id ?? null, reason: 'unknown-item-kind' });
                continue;
            }
            if (typeof line.item_id !== 'string' || line.item_id === '') {
                // Audit C4: offline pharmacy items were written without an id, so every
                // sync called the stock RPC with `undefined` and silently moved nothing.
                anomalies.push({ scope: 'line', id: event.id, itemId: null, reason: 'missing-item-id' });
                continue;
            }
            if (!Number.isInteger(line.qty)) {
                anomalies.push({ scope: 'line', id: event.id, itemId: line.item_id, reason: 'qty-must-be-integer' });
                continue;
            }
            // Only a stock_adjustment may correct downwards; a negative qty anywhere else
            // is the `decrement_drug_stock` hole from the audit, where a negative quantity
            // silently INCREASED stock.
            if (event.kind === 'stock_adjustment') {
                if (line.qty === 0) {
                    anomalies.push({ scope: 'line', id: event.id, itemId: line.item_id, reason: 'qty-must-be-non-zero' });
                    continue;
                }
                applyDelta(line.item_id, line.qty, event.id);
                continue;
            }
            if (line.qty <= 0) {
                anomalies.push({ scope: 'line', id: event.id, itemId: line.item_id, reason: 'qty-must-be-positive' });
                continue;
            }
            applyDelta(line.item_id, event.kind === 'sale' ? -line.qty : line.qty, event.id);
        }
    }

    // Spec §4.4: a negative fold is surfaced — drug, position, and the events involved —
    // as the pharmacy's daily check (`v_stock_anomalies`), exactly as the day-close
    // variance is the drawer's. It is a red row, not a failure.
    for (const [itemId, position] of Object.entries(positions)) {
        if (position < 0) {
            anomalies.push({
                scope: 'position',
                reason: 'negative-position',
                itemId,
                position,
                events: touchedBy.get(itemId) || [],
                crossedAtEventId: crossedAt.get(itemId) || null
            });
        }
    }

    return { positions, anomalies };
}
